import type { AxiosInstance } from 'axios';
/**
 * Per-product KYC document requirements (#59).
 *
 * Endpoints:
 *   GET  /api/v1/contracts/products/{product_id}/kyc-requirements-check
 *   POST /api/v1/workflow/onboarding/kyc-refresh-case
 *
 * Some products demand EXTRA documentation (income proof, fiscal
 * certificate) before a client may originate a NEW contract. The check is
 * LIVE (ratchet — a rule change or document expiry flips it immediately,
 * nothing is grandfathered) and only ever gates new-contract origination:
 * existing contracts and deposits into existing commitments are untouched.
 *
 * The refresh-case endpoint gives the client an open case to upload the
 * missing document against (all uploads are case-scoped): it returns the
 * active case if one exists, otherwise spawns a re-documentation case that
 * inherits their data — the ProductDetail checklist CTA deep-links there.
 */
import authService from './auth.service';

export interface ProductKycRequirement {
  /** Document category ('income', 'fiscal', …). */
  category: string;
  /** Whether the client holds a currently-valid document of this category. */
  current: boolean;
}

export interface ProductKycCheckResult {
  ok: boolean;
  requirements: ProductKycRequirement[];
}

export interface KycRefreshCaseResult {
  case_id: string;
  created: boolean;
}

class ProductKycService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async check(productId: string): Promise<ProductKycCheckResult> {
    const res = await this.api.get<ProductKycCheckResult>(
      `/api/v1/contracts/products/${productId}/kyc-requirements-check`,
    );
    return res.data;
  }

  async startRefreshCase(): Promise<KycRefreshCaseResult> {
    const res = await this.api.post<KycRefreshCaseResult>(
      '/api/v1/workflow/onboarding/kyc-refresh-case',
    );
    return res.data;
  }

  /** Client-facing label per document category. Copy lives here so tone
   *  tweaks ship as frontend-only changes (same rationale as
   *  suitabilityService.describeRequirement). */
  describeCategory(category: string): string {
    switch (category) {
      case 'income':
        return 'Proof of income (recent paystub or income statement)';
      case 'fiscal':
        return 'Tax-status certificate';
      case 'identity':
        return 'Government-issued ID';
      case 'address':
        return 'Proof of address';
      default:
        return 'Additional supporting document';
    }
  }
}

export default new ProductKycService();
